import React, { useEffect, useState } from "react";
import { Row, Col, Collapse, Table, Image } from "antd";
import styles from "./work-details.module.css";
import useFetch from "../../../../../hooks/useFetch";
import { GetSpecificWork } from "../../../../../utils/api.constant";
import { ePriority, eResultCode } from "../../../../../utils/enum";
import useToast from "../../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import {
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EyeOutlined,
  FlagOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Loader from "../../../../../components/loader/loader";
import { formatDistanceToNow } from "date-fns";

interface WorkDetailsModalProps {
  workId: number;
}

const WorkDetailsModal: React.FC<WorkDetailsModalProps> = ({ workId }) => {
  const { post } = useFetch();
  const [workDetails, setWorkDetails] = useState<any>(null);
  const [activeFileKeys, setActiveFileKeys] = useState<string[]>([]);
  const { onShowToast } = useToast();
  const { Panel } = Collapse;

  // Helper function to check if file is an image
  const isImageFile = (fileName: string) => {
    const imageExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".bmp",
      ".webp",
      ".svg",
    ];
    return imageExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
  };

  const fetchSpecifWork = async (workId: number) => {
    try {
      const response = await post(GetSpecificWork, {
        data: {
          id: workId,
        },
      });
      const { data, dataResponse } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setWorkDetails(data);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching project options:", error);
    }
  };

  useEffect(() => {
    if (workId) {
      fetchSpecifWork(workId);
    }
  }, []);

  return (
    <>
      {workDetails ? (
        <div className={styles.workModalContainer}>
          <div className={styles.workHeader}>
            <div className={styles.workTitleContainer}>
              <h2 className={styles.workTitle}>
                {workDetails.workTitle || "Untitled Work"}
              </h2>
              <div className={styles.workCategory}>
                {workDetails.workCategoryName || "No Category"}
              </div>
            </div>

            <div className={styles.workPriority}>
              <span
                className={`${styles.priorityBadge} ${styles[`priority-${workDetails.priority}`]}`}
              >
                {workDetails.priority
                  ? ePriority[workDetails.priority]
                  : "No Priority"}
              </span>
            </div>
          </div>

          <div className={styles.workContent}>
            <div className={styles.descriptionSection}>
              <h3 className={styles.sectionTitle}>Description</h3>
              <div className={styles.descriptionText}>
                {workDetails.workDescription || "No description provided"}
              </div>
            </div>

            <div className={styles.detailsContainer}>
              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>
                  <UserOutlined />
                </div>
                <div>
                  <div className={styles.detailLabel}>Manager</div>
                  <div className={styles.detailValue}>
                    {workDetails.managerName || "Unassigned"}
                  </div>
                </div>
              </div>

              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>
                  <ClockCircleOutlined />
                </div>
                <div>
                  <div className={styles.detailLabel}>Time Left</div>
                  <div className={styles.detailValue}>
                    {workDetails.endDate
                      ? formatDistanceToNow(new Date(workDetails.endDate), {
                          addSuffix: true,
                        })
                      : "Not specified"}
                  </div>
                </div>
              </div>

              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>
                  <CalendarOutlined />
                </div>
                <div>
                  <div className={styles.detailLabel}>Start Date</div>
                  <div className={styles.detailValue}>
                    {workDetails.startDate
                      ? dayjs(workDetails.startDate).format("MMM D, YYYY")
                      : "Not started"}
                  </div>
                </div>
              </div>

              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>
                  <FlagOutlined />
                </div>
                <div>
                  <div className={styles.detailLabel}>End Date</div>
                  <div className={styles.detailValue}>
                    {workDetails.endDate
                      ? dayjs(workDetails.endDate).format("MMM D, YYYY")
                      : "No deadline"}
                  </div>
                </div>
              </div>

              <div className={styles.detailCard}>
                <div className={styles.detailIcon}>
                  <CheckSquareOutlined />
                </div>
                <div>
                  <div className={styles.detailLabel}>Task Count</div>
                  <div className={styles.detailValue}>
                    {workDetails.totalTask || "0"}
                  </div>
                </div>
              </div>
            </div>

            {/* Attachment Accordion */}
            <div className={styles.commentHistory}>
              <Collapse
                activeKey={activeFileKeys}
                onChange={(keys) =>
                  setActiveFileKeys(Array.isArray(keys) ? keys : [keys])
                }
                bordered={false}
              >
                <Panel
                  header={`Attachments (${workDetails?.documents?.length || 0})`}
                  key='1'
                  className={styles.commentAccordion}
                >
                  <div className={styles.responsiveTable}>
                    <Table
                      className={styles.taskTable}
                      columns={[
                        {
                          title: "Preview",
                          dataIndex: "doc_name",
                          key: "preview",
                          width: 80,
                          render: (fileName, record:any) => {
                            const fullUrl = `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}${record.docURL}`;
                            return isImageFile(fileName) ? (
                              <Image
                                width={50}
                                height={50}
                                src={fullUrl}
                                style={{
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                                preview={{
                                  mask: (
                                    <div style={{ fontSize: "12px" }}>
                                      Preview
                                    </div>
                                  ),
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor: "#f5f5f5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  color: "#999",
                                }}
                              >
                                File
                              </div>
                            );
                          },
                        },
                        {
                          title: "File Name",
                          dataIndex: "docName",
                          key: "docName",
                          render: (fileName) => (
                            <div style={{ wordBreak: "break-word" }}>
                              {fileName}
                            </div>
                          ),
                        },
                        {
                          title: "Uploaded By",
                          dataIndex: "createdByUser",
                          key: "createdby",
                          render: (createdByUser) =>
                            createdByUser?.fullName || "-",
                        },
                        {
                          title: "Action",
                          dataIndex: "docURL",
                          key: "docURL",
                          align: "center",
                          render: (url, record) => (
                            <EyeOutlined
                              style={{ cursor: "pointer", fontSize: "16px" }}
                              onClick={() =>
                                window.open(
                                  `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}${url}`,
                                  "_blank"
                                )
                              }
                            />
                          ),
                        },
                      ]}
                      dataSource={workDetails?.documents || []}
                      pagination={{
                        pageSize: 8,
                        showSizeChanger: false,
                        hideOnSinglePage: true,
                      }}
                      rowKey='docURL'
                      scroll={{ x: true }}
                    />
                  </div>
                </Panel>
              </Collapse>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      )}
    </>
  );
};

export default WorkDetailsModal;
